/* External */
import {
  memo, useEffect, useCallback, useState, useRef,
} from 'react';
import PropTypes from 'prop-types';
import { useLocation, useHistory } from 'react-router-dom';
import { withFocusable } from '@noriginmedia/react-spatial-navigation';
import { FormattedMessage } from 'react-intl';

/* Assets */
import Icon from '~/assets/theme/svg/block-content.svg';

/* Utils */
import { getJsonImage } from 'src/theme/images';
import { getLink, Routes } from '../../../../utils/router';
import { useOnBackPress } from '../../../../utils/keyDownEvents';
import { EPG_FILTERS_ID } from '../../../../utils/constants';
import {
  GTM_ERROR_BLOCK_CONTENT,
  formatContentData,
  GTM_CATEGORIES,
  GTM_EVENT_NAMES,
  GTM_SUBCATEGORIES,
} from '../../../../utils/googleTags';

/* Components */
import FocusableButtonprofile from '../../../../components/FocusableButtonProfile';

/* GTM */
import { useGtmLocalContext } from '../../../../providers/GtmLocalProvider';

/* Other */

/* Style */
import './PlayerErrorPage.scss';

function PlayerErrorPage({ realFocusKey, setFocus }) {
  const generalErrorButtonFocusKey = `${realFocusKey}-general-error-button`;
  const location = useLocation();
  const history = useHistory();
  const tagsMountRef = useRef(true);
  const { commonTaggingEvent } = useGtmLocalContext();

  const [errorMessage, setErrorMessage] = useState('');
  const [errorButton, setErrorButton] = useState('');

  const assetContent = location?.state?.content || '';
  const redirectFrom = location.state?.redirectFrom || '';
  const channelId = location.state?.channelIdParentalBlock || '';
  const lastContent = location.state?.lastContent || '';
  const isParentalControl = !!location?.state?.parentalControl;
  const typeError = GTM_ERROR_BLOCK_CONTENT.PARENTAL_CONTROL;

  useEffect(() => {
    if (redirectFrom === 'miniEPG') {
      setErrorMessage(
        <FormattedMessage id="profile.error.parental.message.subtitle.miniEpg" />,
      );
      setErrorButton(
        <FormattedMessage id="profile.error.parental.backChannel" />,
      );
    } else if (redirectFrom === 'liveContent') {
      setErrorMessage(
        <FormattedMessage id="profile.error.parental.message.subtitle.live" />,
      );
      setErrorButton(
        <FormattedMessage id="profile.error.parental.showProgramation" />,
      );
    } else {
      setErrorMessage(
        <FormattedMessage id="profile.error.parental.message.subtitle" />,
      );
      setErrorButton(
        <FormattedMessage id="profile.error.ok" />,
      );
    }
  }, [redirectFrom]);

  const errorProfileCommonTagEvent = useCallback(({ subCategory, eventName }) => {
    const checkEventType = (eventName === GTM_EVENT_NAMES.PAGE_VIEW);
    if (eventName) {
      commonTaggingEvent({
        category: GTM_CATEGORIES.PARENTAL_CONTROL_DATA,
        subCategory,
        eventName,
        data: {
          errorType: typeError || '-',
          errorCode: typeError.toString() || '-',
          ...(checkEventType && {
            content: {
              ...formatContentData({
                content: assetContent,
              }),
            },
          }),
        },
      });
    }
  }, [commonTaggingEvent, assetContent, typeError]);

  useEffect(() => {
    if (tagsMountRef.current && isParentalControl) {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PAGE_VIEW,
        subCategory: GTM_SUBCATEGORIES.BLOCK_CONTENT,
      });
      tagsMountRef.current = false;
    }
  }, [errorProfileCommonTagEvent, isParentalControl]);

  useEffect(() => {
    setFocus(generalErrorButtonFocusKey);
  }, [generalErrorButtonFocusKey, setFocus]);

  const handleClickGoLives = useCallback(() => {
    errorProfileCommonTagEvent({
      eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_BLOCK_CONTENT.CONSULT_SCHEDULE,
      subCategory: GTM_SUBCATEGORIES.BLOCK_CONTENT,
    });
    sessionStorage.setItem('parentalBlockedChannelId', channelId);
    sessionStorage.setItem('selectedFilterEpg', JSON.stringify(EPG_FILTERS_ID.ALL_STATIONS));
    history.push(getLink(Routes.Private.Live));
  }, [history, channelId, errorProfileCommonTagEvent]);

  const handleClickGoBack = useCallback(() => {
    if (redirectFrom === 'liveContent') {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_BLOCK_CONTENT.CONSULT_SCHEDULE,
        subCategory: GTM_SUBCATEGORIES.BLOCK_CONTENT,
      });
      sessionStorage.setItem('parentalBlockedChannelId', channelId);
      sessionStorage.setItem('selectedFilterEpg', JSON.stringify(EPG_FILTERS_ID.ALL_STATIONS));

      history.push(getLink(Routes.Private.Live));
    } else if (redirectFrom === 'miniEPG') {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_BLOCK_CONTENT.BACK,
        subCategory: GTM_SUBCATEGORIES.BLOCK_CONTENT,
      });
      history.replace(lastContent, {
        redirectFrom: 'parentalBlock',
      });
    } else {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_BLOCK_CONTENT.OK,
        subCategory: GTM_SUBCATEGORIES.BLOCK_CONTENT,
      });
      history.goBack();
    }
  }, [history, channelId, lastContent, redirectFrom, errorProfileCommonTagEvent]);

  useOnBackPress({
    listener: () => {
      errorProfileCommonTagEvent({
        eventName: GTM_EVENT_NAMES.PARENTAL_CONTROL_BLOCK_CONTENT.CLOSE,
        subCategory: GTM_SUBCATEGORIES.BLOCK_CONTENT,
      });
      history.goBack();
    },
  });

  return (
    <div className="dtv-html5-c-general-errors">
      <div className="dtv-html5-c-general-errors__center">
        <div className="dtv-html5-c-general-errors__limit">
          <img src={getJsonImage('block-content') || Icon} alt="dtv Blocked Content" className="dtv-html5-c-general-errors__parental_control_error" />
          <div className="dtv-html5-c-general-errors__text-container">
            <div className="dtv-html5-c-general-errors__title">
              <FormattedMessage id="profile.error.parental.message.title" />
            </div>
            <p className="dtv-html5-c-general-errors__subtitle">
              {errorMessage}
            </p>
          </div>
        </div>
      </div>
      <div className="dtv-html5-c-general-errors__botton">
        <div className="dtv-html5-c-general-errors__buttonAlignment">
          <FocusableButtonprofile
            type="submit"
            secondGray
            bordered
            focusKey={generalErrorButtonFocusKey}
            onEnterPress={handleClickGoBack}
          >
            {errorButton}
          </FocusableButtonprofile>
        </div>
        {redirectFrom === 'miniEPG' && (
          <div className="dtv-html5-c-general-errors__buttonAlignment">
            <FocusableButtonprofile
              type="submit"
              secondGray
              bordered
              focusKey
              onEnterPress={handleClickGoLives}
            >
              <FormattedMessage id="profile.error.parental.showProgramation" />
            </FocusableButtonprofile>
          </div>
        )}
      </div>
    </div>
  );
}

PlayerErrorPage.propTypes = {
  realFocusKey: PropTypes.string.isRequired,
  setFocus: PropTypes.func.isRequired,
};

export default memo(withFocusable()(memo(PlayerErrorPage)));
